import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

type BtnVariant = "primary" | "ghost" | "danger" | "success" | "tg";

export function Button({
  variant = "primary",
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant }) {
  const base =
    "inline-flex items-center justify-center gap-1.5 h-8 px-3.5 text-xs font-semibold rounded-lg border whitespace-nowrap transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed";
  const v =
    variant === "primary"
      ? "bg-brand/15 text-brand border-brand/30 hover:bg-brand-grad hover:text-brand-fg hover:border-transparent hover:shadow-glow"
      : variant === "danger"
        ? "bg-red-900/40 text-red-300 border-red-700/50 hover:bg-red-500 hover:text-white hover:border-red-500"
        : variant === "success"
          ? "bg-emerald-900/40 text-emerald-300 border-emerald-700/50 hover:bg-emerald-500 hover:text-white hover:border-emerald-500"
          : variant === "tg"
            ? "bg-sky-900/40 text-sky-300 border-sky-700/50 hover:bg-sky-500 hover:text-white hover:border-sky-500"
            : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:bg-zinc-700 hover:text-white hover:border-zinc-600";
  return <button className={`${base} ${v} ${className}`} {...rest} />;
}

export function Input({
  className = "",
  ...rest
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`h-9 w-full px-3.5 rounded-lg bg-black/25 border border-white/[0.08] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/20 transition ${className}`}
      {...rest}
    />
  );
}

export function NumericInput({
  className = "",
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type">) {
  return (
    <Input
      type="text"
      inputMode="decimal"
      pattern="[0-9]*"
      className={`${className} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
      {...rest}
    />
  );
}

export function Card({
  title,
  children,
  className = "",
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-2xl card-elev p-4 md:p-5 ${className}`}>
      {title && (
        <div className="text-zinc-400 text-xs uppercase tracking-wider mb-3">{title}</div>
      )}
      {children}
    </div>
  );
}

export function PageHeader({ title, right }: { title: string; right?: ReactNode }) {
  return (
    <header className="flex items-start sm:items-center justify-between mb-5 md:mb-6 pb-4 border-b border-zinc-800 gap-3 flex-wrap">
      <h1 className="text-xl md:text-2xl font-bold flex items-center gap-3 min-w-0 tracking-tight">
        <span className="w-1.5 h-5 md:h-7 bg-brand-grad rounded-full shrink-0 shadow-glow" />
        <span className="truncate">{title}</span>
      </h1>
      {right}
    </header>
  );
}

export function Pill({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "success" | "warning" | "danger";
  children: ReactNode;
}) {
  const c =
    tone === "success"
      ? "bg-emerald-900/50 text-emerald-300 border-emerald-700/50"
      : tone === "warning"
        ? "bg-amber-900/40 text-amber-300 border-amber-700/50"
        : tone === "danger"
          ? "bg-red-900/40 text-red-300 border-red-700/50"
          : "bg-zinc-800 text-zinc-300 border-zinc-700";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs border ${c}`}>
      {children}
    </span>
  );
}

/** Меню «⋯» в строке таблицы. Рендерится порталом в body: обёртка Table имеет
 *  overflow-x-auto (нужен для горизонтальной прокрутки) и обрезала бы выпадашку. */
export function RowActions({
  children,
  label = "⋯",
}: {
  children: (close: () => void) => ReactNode;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onScrollResize = () => setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="w-8 h-8 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.06] transition-colors leading-none"
        aria-label="Действия"
      >
        {label}
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: pos.top, right: pos.right, zIndex: 60 }}
            onClick={(e) => e.stopPropagation()}
            className="w-max min-w-[210px] max-w-[300px] rounded-xl menu-surface p-1 origin-top-right animate-[popIn_.12s_ease-out]"
          >
            {children(() => setOpen(false))}
          </div>,
          document.body,
        )}
    </>
  );
}

export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/[0.07] overflow-x-auto card-elev">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

export function Th({
  children,
  align = "left",
  fit = false,
}: {
  children: ReactNode;
  align?: "left" | "right";
  fit?: boolean;
}) {
  const a = align === "right" ? "text-right" : "text-left";
  return (
    <th
      className={`${a} text-xs uppercase tracking-wider text-zinc-400 font-medium px-4 py-2 bg-zinc-900/60 border-b border-zinc-800 ${
        fit ? "w-px whitespace-nowrap" : ""
      }`}
    >
      {children}
    </th>
  );
}

export function SortableTh({
  children,
  sortKey,
  currentSort,
  currentDir,
  onSort,
}: {
  children: ReactNode;
  sortKey: string;
  currentSort: string | null;
  currentDir: "asc" | "desc";
  onSort: (key: string) => void;
}) {
  const active = currentSort === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`cursor-pointer select-none text-left text-xs uppercase tracking-wider font-medium px-4 py-2 bg-zinc-900/60 border-b border-zinc-800 hover:bg-zinc-800/60 transition-colors ${
        active ? "text-brand" : "text-zinc-400"
      }`}
    >
      <span className="inline-flex items-center gap-1.5">
        {children}
        <SortArrow active={active} dir={currentDir} />
      </span>
    </th>
  );
}

function SortArrow({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  if (!active) {
    return (
      <svg width="10" height="12" viewBox="0 0 10 12" className="opacity-30 shrink-0">
        <path d="M5 2 L8 5 L2 5 Z" fill="currentColor" />
        <path d="M5 10 L2 7 L8 7 Z" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" className="shrink-0">
      {dir === "desc" ? (
        <path d="M5 8 L1 3 L9 3 Z" fill="currentColor" />
      ) : (
        <path d="M5 2 L9 7 L1 7 Z" fill="currentColor" />
      )}
    </svg>
  );
}

export function Td({
  children,
  className = "",
  align = "left",
  fit = false,
}: {
  children: ReactNode;
  className?: string;
  align?: "left" | "right";
  fit?: boolean;
}) {
  const a = align === "right" ? "text-right" : "text-left";
  return (
    <td
      className={`px-4 py-3 border-b border-zinc-900 ${a} ${
        fit ? "w-px whitespace-nowrap" : ""
      } ${className}`}
    >
      {children}
    </td>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label
      className={`inline-flex items-center gap-2.5 text-sm text-zinc-300 select-none ${
        disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <span className="relative inline-flex">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="peer appearance-none w-4 h-4 rounded border border-zinc-700 bg-zinc-900 checked:bg-brand checked:border-brand hover:border-zinc-500 checked:hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-brand/30 transition-colors cursor-pointer disabled:cursor-not-allowed"
        />
        <svg
          className="pointer-events-none absolute inset-0 m-auto w-3 h-3 text-white opacity-0 peer-checked:opacity-100"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 6 L5 9 L10 3" />
        </svg>
      </span>
      {label}
    </label>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
  maxWidth = "max-w-xl",
  elevated = false,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: string;
  // elevated — модалка, открытая ПОВЕРХ другой модалки (напр. карточка ноды из
  // карточки хоста). Без этого обе на z-50 и вложенная лезет ПОД родителя.
  elevated?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", h);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className={`fixed inset-0 ${elevated ? "z-[60]" : "z-50"} flex items-end sm:items-center justify-center p-0 sm:p-4`}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative w-full ${maxWidth} rounded-t-2xl sm:rounded-2xl card-elev shadow-pop max-h-[92vh] sm:max-h-[85vh] flex flex-col`}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/[0.06]">
          <h3 className="text-base sm:text-lg font-semibold text-zinc-50 truncate pr-4 flex items-center gap-2.5">
            <span className="w-1 h-4 rounded-full bg-brand-grad shrink-0" />
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200 text-xl leading-none w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/[0.06] shrink-0 transition-colors"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>
        <div className="p-4 sm:p-6 overflow-auto">{children}</div>
      </div>
    </div>
  );
}

export function TgLink({
  username,
  className = "",
  children,
}: {
  username: string | null | undefined;
  className?: string;
  children?: ReactNode;
}) {
  if (!username) return <span className="text-zinc-500">—</span>;
  return (
    <a
      href={`https://t.me/${username}`}
      target="_blank"
      rel="noreferrer"
      className={`text-zinc-100 hover:text-brand hover:underline ${className}`}
    >
      {children ?? `@${username}`}
    </a>
  );
}

// Тёмный стилизованный дропдаун (замена нативному <select> — тот рендерит
// белый нативный попап поверх тёмной темы). Поиск появляется при >8 опций.
export function DarkSelect({
  value,
  onChange,
  options,
  placeholder = "— выбрать —",
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string; hint?: string }[];
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Список рисуем порталом: раньше absolute-выпадашку обрезали карточки и
  // модалки с overflow (в «Выдать доступ» было видно только первую строку).
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const top = below < 240 && r.top > below ? Math.max(8, r.top - 296) : r.bottom + 4;
    setPos({ top, left: r.left, width: r.width });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onScrollResize = () => setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open]);

  const sel = options.find((o) => o.value === value);
  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : options;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full h-9 px-3 rounded-lg bg-black/25 border text-sm text-left flex items-center justify-between transition ${
          open ? "border-brand/60 ring-2 ring-brand/20" : "border-white/[0.08] hover:border-brand/40"
        } ${className}`}
      >
        <span className={sel ? "text-zinc-100 truncate" : "text-zinc-500"}>
          {sel?.label ?? placeholder}
        </span>
        <svg
          className={`text-zinc-500 ml-2 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: pos.top, left: pos.left, minWidth: pos.width, zIndex: 70 }}
            className="rounded-xl menu-surface max-h-72 overflow-auto p-1 animate-[popIn_.12s_ease-out]"
          >
            {options.length > 8 && (
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="поиск…"
                className="w-full h-9 px-3 bg-black/40 border-b border-white/[0.06] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none sticky top-0"
              />
            )}
            {filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                  setQ("");
                }}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors hover:bg-white/[0.07] ${
                  o.value === value ? "text-brand bg-brand-soft/40" : "text-zinc-300"
                }`}
              >
                <div className="text-sm">{o.label}</div>
                {o.hint && <div className="text-[11px] text-zinc-500 mt-0.5">{o.hint}</div>}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-zinc-600">ничего не найдено</div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

// Единый дропдаун проекта. Select — псевдоним DarkSelect: раньше рядом жили
// два почти одинаковых компонента, что и рождало разнобой в интерфейсе.
export const Select = DarkSelect;
